import { auth } from "../firebase/config";
import AIService from "./AIService";
import {
  AppPerformanceAIAssessment,
  AppPerformanceCatalogResponse,
  AppPerformanceSnapshot,
  PerformanceVerdict,
} from "../types/appPerformance";

const FETCH_APP_PERFORMANCE_ENDPOINT =
  process.env.REACT_APP_FETCH_APP_PERFORMANCE_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/fetchAppPerformanceCatalog";
const SYNC_APP_PERFORMANCE_SIGNALS_ENDPOINT =
  process.env.REACT_APP_SYNC_APP_PERFORMANCE_SIGNALS_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/syncAppPerformanceSignalsFromBigQuery";

class AppPerformanceService {
  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>,
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("Authentication required. Please sign in.");
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      throw new Error(
        detailedMessage || `Request failed with status ${response.status}`,
      );
    }

    return responseData as T;
  }

  async fetchCatalog(
    periodDays = 30,
    appIds: string[] = [],
  ): Promise<AppPerformanceCatalogResponse> {
    const normalizedPeriod = [7, 30, 90].includes(periodDays) ? periodDays : 30;
    const normalizedAppIds = Array.from(
      new Set(
        (Array.isArray(appIds) ? appIds : [])
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      ),
    );
    return this.postAuthenticatedJson<AppPerformanceCatalogResponse>(
      FETCH_APP_PERFORMANCE_ENDPOINT,
      { periodDays: normalizedPeriod, appIds: normalizedAppIds },
    );
  }

  async syncSignals(periodDays = 30, appIds: string[] = []): Promise<{
    success: boolean;
    periodDays: number;
    syncedCount: number;
    errorCount: number;
    skippedCount: number;
    message?: string;
  }> {
    const normalizedPeriod = [7, 30, 90].includes(periodDays) ? periodDays : 30;
    const normalizedAppIds = Array.from(
      new Set(
        (Array.isArray(appIds) ? appIds : [])
          .map((item) => String(item || "").trim())
          .filter(Boolean),
      ),
    );
    return this.postAuthenticatedJson(SYNC_APP_PERFORMANCE_SIGNALS_ENDPOINT, {
      periodDays: normalizedPeriod,
      appIds: normalizedAppIds,
    });
  }

  private inferFallbackVerdict(snapshot: AppPerformanceSnapshot): PerformanceVerdict {
    const downloads = snapshot.combined.downloads.value || 0;
    const crashRate = snapshot.combined.crashRate.value;
    const retention = snapshot.combined.retentionDay7.value;

    if (crashRate !== null && crashRate > 3.5) return "critical";
    if (retention !== null && retention < 8) return "critical";
    if (downloads >= 5000 && (crashRate === null || crashRate < 2.5)) {
      return "healthy";
    }
    return "watch";
  }

  async evaluateWithAI(
    snapshot: AppPerformanceSnapshot,
    periodDays: number,
  ): Promise<AppPerformanceAIAssessment> {
    const prompt = [
      "You are a mobile growth analyst.",
      `Evaluate app performance for ${snapshot.appName} over the last ${periodDays} days.`,
      "Return strict JSON with keys: verdict, score, summary, keyDrivers, nextActions.",
      "verdict must be one of: strong, healthy, watch, critical.",
      "score must be 0-100.",
      "keyDrivers and nextActions must each be arrays with 2-4 concise strings.",
      "Use the metrics and diagnostics below:",
      JSON.stringify(snapshot),
    ].join("\n");

    try {
      const ai = await AIService.generateJSON<AppPerformanceAIAssessment>(prompt, {
        model: AIService.getDefaultModel(),
        temperature: 0.2,
        maxTokens: 700,
      });

      const verdictValues: PerformanceVerdict[] = [
        "strong",
        "healthy",
        "watch",
        "critical",
      ];
      const verdict = verdictValues.includes(ai?.verdict)
        ? ai.verdict
        : this.inferFallbackVerdict(snapshot);
      const score = Number.isFinite(Number(ai?.score))
        ? Math.max(0, Math.min(100, Number(ai.score)))
        : 50;

      return {
        verdict,
        score,
        summary:
          String(ai?.summary || "").trim() ||
          "Performance requires manual review based on available metrics.",
        keyDrivers:
          Array.isArray(ai?.keyDrivers) && ai.keyDrivers.length
            ? ai.keyDrivers.slice(0, 4).map((item) => String(item))
            : ["Insufficient metric coverage for detailed drivers."],
        nextActions:
          Array.isArray(ai?.nextActions) && ai.nextActions.length
            ? ai.nextActions.slice(0, 4).map((item) => String(item))
            : ["Connect full analytics pipelines for stronger recommendations."],
      };
    } catch {
      return {
        verdict: this.inferFallbackVerdict(snapshot),
        score: 50,
        summary:
          "AI assessment fallback used because a structured AI response was unavailable.",
        keyDrivers: ["Metric coverage is partial for one or more stores."],
        nextActions: [
          "Review diagnostics and complete store reporting connectors.",
        ],
      };
    }
  }
}

const appPerformanceService = new AppPerformanceService();
export default appPerformanceService;
